import {useCallback, useMemo, useState} from 'react';

type UseSelectedItemsHookType<IdType> = {
    selectedKeys: Array<IdType>;
    isInverted: boolean;
    toggleRow: (key: IdType) => void;
    selectAll: () => void;
    deselectAll: () => void;
    toggleAll: () => void;
    isRowSelected: (key: IdType) => boolean;
    clear: () => void;
    pruneSelected: (predicate: (key: IdType) => boolean) => void;
    selectedCount: number;
};

export function useSelectedItems<IdType extends string | number | null | {id: number} = string>(
    total: number
): UseSelectedItemsHookType<IdType> {
    const [selectedKeys, setSelectedKeys] = useState<Array<IdType>>([]);
    const [isInverted, setIsInverted] = useState(false);

    const toggleRow = useCallback(
        // eslint-disable-next-line sonarjs/cognitive-complexity
        (key: IdType) => {
            const isSelected =
                typeof key === 'object' && key !== null && 'id' in key
                    ? selectedKeys.some(
                          (item) =>
                              typeof item === 'object' &&
                              item !== null &&
                              'id' in item &&
                              (item as {id: number}).id === (key as {id: number}).id
                      )
                    : selectedKeys.includes(key);

            if (isInverted) {
                if (isSelected) {
                    setSelectedKeys((previous) =>
                        typeof key === 'object' && key !== null && 'id' in key
                            ? previous.filter(
                                  (item) =>
                                      !(
                                          typeof item === 'object' &&
                                          item !== null &&
                                          'id' in item &&
                                          (item as {id: number}).id === (key as {id: number}).id
                                      )
                              )
                            : previous.filter((item) => item !== key)
                    );
                } else {
                    setSelectedKeys((previous) => [...previous, key]);
                }
            } else if (isSelected) {
                setSelectedKeys((previous) =>
                    typeof key === 'object' && key !== null && 'id' in key
                        ? previous.filter(
                              (item) =>
                                  !(
                                      typeof item === 'object' &&
                                      item !== null &&
                                      'id' in item &&
                                      (item as {id: number}).id === (key as {id: number}).id
                                  )
                          )
                        : previous.filter((item) => item !== key)
                );
            } else {
                setSelectedKeys((previous) => [...previous, key]);
            }
        },
        [isInverted, selectedKeys]
    );

    const selectAll = useCallback(() => {
        setIsInverted(true);
        setSelectedKeys([]);
    }, []);

    const deselectAll = useCallback(() => {
        setIsInverted(false);
        setSelectedKeys([]);
    }, []);

    const isRowSelected = useCallback(
        (key: IdType) => {
            const isSelected =
                typeof key === 'object' && key !== null && 'id' in key
                    ? selectedKeys.some(
                          (item) =>
                              typeof item === 'object' &&
                              item !== null &&
                              'id' in item &&
                              (item as {id: number}).id === (key as {id: number}).id
                      )
                    : selectedKeys.includes(key);

            if (isInverted) {
                return !isSelected;
            }

            return isSelected;
        },
        [isInverted, selectedKeys]
    );

    const clear = useCallback(() => {
        setSelectedKeys([]);
        setIsInverted(false);
    }, []);

    const pruneSelected = useCallback((predicate: (key: IdType) => boolean) => {
        setSelectedKeys((previous) => previous.filter(predicate));
        setIsInverted(false);
    }, []);

    const selectedCount = useMemo(() => {
        return isInverted ? (total || 0) - selectedKeys.length : selectedKeys.length;
    }, [isInverted, selectedKeys.length, total]);

    const toggleAll = useCallback(() => {
        if (selectedCount) {
            deselectAll();
            return;
        }

        selectAll();
    }, [deselectAll, selectAll, selectedCount]);

    return {
        selectedKeys,
        isInverted,
        toggleRow,
        selectAll,
        deselectAll,
        isRowSelected,
        clear,
        pruneSelected,
        selectedCount,
        toggleAll,
    };
}
